import * as React from "react";
import { Search, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { ListItem } from "../ListItem";
import { Button } from "../Button";
import { Divider } from "../Divider";
import { Text } from "../Text";
import { Link } from "../Link";

export interface DropdownMenuItem {
  id: string | number;
  title: string;
  description?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
}

export interface DropdownMenuProps {
  /**
   * Items to display in the dropdown
   */
  items?: DropdownMenuItem[];
  /**
   * Custom content to render instead of the default item list
   */
  customContent?: React.ReactNode;
  /**
   * Section heading text
   */
  sectionHeading?: string;
  /**
   * Whether the dropdown is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether to show empty/no results state
   */
  isEmpty?: boolean;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Empty state link text
   */
  emptyLinkText?: string;
  /**
   * Empty state link click handler
   */
  onEmptyLinkClick?: () => void;
  /**
   * Primary button text
   */
  primaryButtonText?: string;
  /**
   * Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Primary button click handler
   */
  onPrimaryClick?: () => void;
  /**
   * Secondary button click handler
   */
  onSecondaryClick?: () => void;
  /**
   * Show chevron on list items
   */
  showChevron?: boolean;
  /**
   * Custom empty state icon
   */
  emptyIcon?: React.ReactNode;
  /**
   * Disable footer buttons
   */
  disableFooter?: boolean;
  /**
   * Whether to show the footer actions (overrides disableFooter)
   */
  showFooter?: boolean;
  /**
   * Footer layout orientation
   */
  footerLayout?: "horizontal" | "vertical";
  /**
   * Callback when menu should close
   */
  onClose?: () => void;
  /**
   * Focused item index (for keyboard navigation)
   */
  focusedIndex?: number;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Width of the dropdown menu
   */
  width?: "auto" | "full" | string;
}

export const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      items = [],
      customContent,
      sectionHeading,
      isLoading = false,
      isEmpty = false,
      emptyTitle = "No Search Results Found",
      emptyDescription = "Add description of what the user can search for here.",
      emptyLinkText = "Link to support site",
      onEmptyLinkClick,
      primaryButtonText = "Primary",
      secondaryButtonText = "Secondary",
      onPrimaryClick,
      onSecondaryClick,
      showChevron = false,
      emptyIcon,
      disableFooter = false,
      showFooter,
      footerLayout = "horizontal",
      onClose,
      focusedIndex = -1,
      className,
      width = "auto",
    },
    ref
  ) => {
    const renderContent = () => {
      if (isLoading) {
        return (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <Loader2 className="w-12 h-12 text-action-ink-primary-normal mb-4 animate-spin" />
          </div>
        );
      }

      if (customContent) {
        return (
          <div className="py-3 px-3 max-h-[400px] overflow-y-auto">
            {sectionHeading && (
              <Text
                as="div"
                variant="body"
                size="small"
                weight="medium"
                className="text-body-small-medium text-surface-ink-neutral-muted px-3 py-2 mb-1"
              >
                {sectionHeading}
              </Text>
            )}
            <div className="px-1">{customContent}</div>
          </div>
        );
      }

      if (isEmpty || items.length === 0) {
        return (
          <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
            {emptyIcon || (
              <Search className="w-12 h-12 text-surface-ink-neutral-muted mb-4" />
            )}
            <Text
              as="h3"
              variant="body"
              size="small"
              weight="semibold"
              className="text-surface-ink-neutral-normal mb-2"
            >
              {emptyTitle}
            </Text>
            <Text
              as="p"
              variant="body"
              size="small"
              weight="regular"
              className="text-surface-ink-neutral-muted mb-3"
            >
              {emptyDescription}
            </Text>
            {emptyLinkText && (
              <Link
                type="anchor"
                color="primary"
                size="small"
                onClick={onEmptyLinkClick}
              >
                {emptyLinkText}
              </Link>
            )}
          </div>
        );
      }

      return (
        <div className="py-3 px-3 max-h-[400px] overflow-y-auto">
          {sectionHeading && (
            <Text
              as="div"
              variant="body"
              size="small"
              weight="medium"
              className="text-surface-ink-neutral-muted px-3 py-2 mb-1"
            >
              {sectionHeading}
            </Text>
          )}
          <div className="flex flex-col gap-1">
            {items.map((item, index) => (
              <ListItem
                key={item.id}
                title={item.title}
                description={item.description}
                leadingIcon={item.leadingIcon}
                trailingIcon={item.trailingIcon}
                showChevron={showChevron}
                isDisabled={item.isDisabled}
                isSelected={index === focusedIndex}
                onClick={() => {
                  item.onClick?.();
                  onClose?.();
                }}
                containerClassName={cn(
                  index === focusedIndex && "bg-action-fill-primary-faded"
                )}
              />
            ))}
          </div>
        </div>
      );
    };

    const widthClass =
      width === "full" ? "w-full" : width === "auto" ? "w-auto" : "";
    const footerVisible = showFooter ?? !disableFooter;

    return (
      <div
        ref={ref}
        className={cn(
          "bg-white  rounded-large overflow-hidden",
          widthClass,
          className
        )}
        style={{
          boxShadow:
            "0 1px 2px rgba(25, 25, 30, 0.1), 0 2px 6px rgba(25, 25, 30, 0.06)",
          ...(width !== "full" && width !== "auto" ? { width } : {}),
        }}
      >
        {/* Content */}
        {renderContent()}

        {/* Footer */}
        {footerVisible && (
          <div className="flex flex-col">
            <Divider thickness="thin" variant="muted" />
            <div
              className={cn(
                "flex gap-3 p-4",
                footerLayout === "vertical"
                  ? "flex-col"
                  : "items-center flex-row"
              )}
            >
              <Button
                variant="secondary"
                color="primary"
                size="medium"
                isFullWidth
                onClick={onSecondaryClick}
              >
                {secondaryButtonText}
              </Button>
              <Button
                variant="primary"
                color="primary"
                size="medium"
                isFullWidth
                onClick={onPrimaryClick}
              >
                {primaryButtonText}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

DropdownMenu.displayName = "DropdownMenu";
